import React, { useEffect, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, History } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';
import ReviewHistory from './ReviewHistory';
import { deciderOf, fmt, ts } from './reviewHistoryShared';

/**
 * "Changes requested" banner shown at the top of the assessment editor whenever
 * the assessment sits in the `denied` review state.
 *
 * This is the payoff of a rejection notification: the submitter clicks through
 * from the bell and lands here with the reviewer's reason already on screen, so
 * they can start making the required changes without hunting through the review
 * history modal. It surfaces the latest denial's reason, who requested it, and
 * when — pulled from the same ReviewSubmission records the History modal uses.
 *
 * The banner is not manually dismissible: it clears on its own once the
 * assessment leaves the `denied` state (i.e. after the user resubmits), so a
 * pending change request can never be permanently hidden and forgotten.
 *
 * Props:
 *   assessmentId - numeric assessment pk (object_id for the history endpoint)
 *   entityName   - assessment name, for the history modal header
 *   status       - current review status; banner renders only when 'denied'
 */
const latestDenial = (rows) => {
  const denials = (rows || [])
    .filter((r) => r.status === 'denied' && r.is_decided && r.decision_reason)
    .sort((a, b) => ts(b.decided_at) - ts(a.decided_at));
  return denials[0] || null;
};

const ChangesRequestedBanner = ({ assessmentId, entityName, status }) => {
  const [denial, setDenial] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isDenied = status === 'denied';

  useEffect(() => {
    if (!isDenied || !assessmentId) {
      setDenial(null);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const data = await getReviewHistory('assessment', assessmentId);
        const rows = Array.isArray(data) ? data : data?.results || [];
        if (active) setDenial(latestDenial(rows));
      } catch {
        // Non-fatal: the status pill still tells the user changes were requested.
        if (active) setDenial(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [isDenied, assessmentId]);

  if (!isDenied) return null;

  const who = denial ? deciderOf(denial).name : null;
  const when = denial?.decided_at ? fmt(denial.decided_at) : null;

  return (
    <div className="border-b bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200">
      <div className="flex items-start gap-2 px-4 py-2 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">Changes requested</span>
            {(who || when) && (
              <span className="text-xs opacity-80">
                {who ? `by ${who}` : ''}
                {who && when ? ' · ' : ''}
                {when || ''}
              </span>
            )}
            {denial?.decision_reason && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="ml-auto inline-flex items-center gap-1 text-xs font-medium hover:underline"
                title={expanded ? 'Hide reason' : 'Show reason'}
              >
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {expanded ? 'Hide' : 'Show'} reason
              </button>
            )}
          </div>
          {expanded && denial?.decision_reason && (
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-red-700 dark:text-red-200">
              {denial.decision_reason}
            </p>
          )}
          <div className="mt-1">
            <button
              onClick={() => setHistoryOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"
            >
              <History className="w-3.5 h-3.5" />
              View full history
            </button>
          </div>
        </div>
      </div>

      {historyOpen && (
        <ReviewHistory
          entityType="assessment"
          objectId={assessmentId}
          entityName={entityName}
          currentStatus={status}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
};

export default ChangesRequestedBanner;
